import { useEffect, useState } from 'react';

import { sourceKeyOf, type DocumentSource } from './documentSource';
import { fetchDocumentBytes } from './fetchDocumentBytes';

type ObjectUrlState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error' };

/**
 * A document's bytes as a local `blob:` URL, for the viewers that need a `src`
 * rather than a parsed document.
 *
 * The image viewer takes a URL, and the one it must not be given is the
 * document's own: the bytes may need the session token, and the bucket is not
 * readable cross-origin anyway. Fetching first and handing over a blob URL
 * makes the image same-origin, which sidesteps both.
 *
 * The type is deliberately not restated. A mislabelled blob matters where the
 * browser is asked to render it by type — an `octet-stream` PDF downloads
 * instead of displaying — but here the caller has already decided this is an
 * image, and the browser sniffs an `<img>` source regardless of what the blob
 * claims to be.
 */
export const useDocumentObjectUrl = (
  source: DocumentSource,
  isEnabled: boolean,
): ObjectUrlState => {
  const [state, setState] = useState<ObjectUrlState>({ status: 'idle' });
  const sourceKey = sourceKeyOf(source);

  useEffect(() => {
    // An empty key is a document with no address at all, and `fetch('')`
    // resolves against the current page — it would read the app's own HTML as
    // if it were the image.
    if (!isEnabled || sourceKey === '') return;

    const controller = new AbortController();
    // An object URL pins its blob for the lifetime of the document, so every
    // one created here must be revoked when the viewer goes away.
    let objectUrl = '';

    const load = async () => {
      setState({ status: 'loading' });

      try {
        const bytes = await fetchDocumentBytes(source, controller.signal);
        objectUrl = URL.createObjectURL(new Blob([bytes]));
        setState({ status: 'ready', url: objectUrl });
      } catch {
        // An abort is the viewer closing, not a failure worth showing.
        if (!controller.signal.aborted) setState({ status: 'error' });
      }
    };

    void load();

    return () => {
      controller.abort();
      if (objectUrl !== '') URL.revokeObjectURL(objectUrl);
    };
    // `source` is intentionally absent: `sourceKey` is its value identity, and
    // depending on the object itself would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by sourceKey, see above
  }, [sourceKey, isEnabled]);

  // Derived rather than held, so a closed viewer never reports a URL the
  // cleanup above has already revoked.
  if (!isEnabled) return { status: 'idle' };
  // Nothing to fetch means nothing will ever arrive, so the caller is told to
  // fall back rather than being left waiting on a load that never starts.
  return sourceKey === '' ? { status: 'error' } : state;
};
